import { type CSSProperties, type ReactNode } from 'react';
import { useLingui } from '@lingui/react/macro';
import { Heart, Zap } from 'lucide-react';
import { cn } from '@worldcoo/ui/lib/utils';
import { WorldcooLogoIcon } from '@worldcoo/ui/custom/worldcoo-logo-icon';
import { Image } from '@/routes/-components/image';
import { CoinsTileIllustration } from '@worldcoo/ui/illustrations/animated/coins-tile-illustration';
import { FLUID_FIGURE } from './stat-figure';

/**
 * `style` must be forwarded: `Expand` with `asChild` passes the box's place in the arrival sweep as an inline
 * custom property, and a tile that drops it arrives with every other box at once.
 */
export type TileProps = { className?: string; style?: CSSProperties };

/**
 * `valueClassName` takes `FLUID_FIGURE` for the two figures shown in full; eleven digits do not fit the flat
 * size four do. `tabular-nums` keeps a rolling figure from resizing its tile digit by digit.
 */
export function StatValue(props: { value: ReactNode; label: string; valueClassName?: string }) {
	return (
		<>
			{/* Hand-off sizes, from `md` only: below it these are a 17rem x 11rem swipe row it never draws. */}
			<p className={cn('text-4xl font-semibold tabular-nums md:text-[3.5rem]/[1.05]', props.valueClassName)}>
				{props.value}
			</p>
			<p className="mt-2 text-sm font-medium md:text-2xl">{props.label}</p>
		</>
	);
}

export function FundedProjectsTile(props: TileProps & { value: string }) {
	const { t } = useLingui();
	return (
		<div
			style={props.style}
			className={cn('bg-apricot text-ink flex flex-col rounded-3xl p-6 md:p-8', props.className)}
		>
			<Zap className="text-brand-strong size-6 fill-current" />
			<div className="mt-auto pt-6">
				<StatValue value={props.value} label={t`Funded projects`} />
			</div>
		</div>
	);
}

export function CoinsTile(props: TileProps) {
	const { t } = useLingui();
	return (
		<div style={props.style} className={cn('bg-sand relative overflow-hidden rounded-3xl', props.className)}>
			<CoinsTileIllustration label={t`Hands catching euro coins`} className="absolute inset-0 h-full w-full" />
		</div>
	);
}

export function TotalRaisedTile(props: TileProps & { value: ReactNode }) {
	const { t } = useLingui();
	return (
		<div style={props.style} className={cn('relative overflow-hidden rounded-3xl', props.className)}>
			{/* The wide-screen box (`h-136`, (1200 - 2 x 16) / 3 wide): srcset derives from it, so smaller blurs.
			    Dev serves originals with no imgproxy, so it looks sharp there regardless. */}
			<Image
				src="/redesign/hero-hands.jpg"
				alt={t`Two hands holding each other`}
				height={544}
				aspectRatio={390 / 544}
				objectFit="cover"
				className="absolute inset-0 h-full w-full object-cover!"
			/>
			{/* The padded box, so `100cqi` is the figure's real room. */}
			<div className="@container absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-6 text-white md:p-8">
				<StatValue value={props.value} label={t`Total raised`} valueClassName={FLUID_FIGURE} />
			</div>
		</div>
	);
}

export function MicrodonationsTile(props: TileProps & { value: ReactNode }) {
	const { t } = useLingui();
	return (
		<div
			style={props.style}
			className={cn(
				// Here, because this element carries the padding and its content box is the figure's real room.
				'@container bg-brand text-ink relative flex flex-col justify-end overflow-hidden rounded-3xl p-6 md:p-8',
				props.className,
			)}
		>
			{/* Percentages, not the donors app's pixels: this tile is portrait in the grid, landscape on mobile. */}
			<WorldcooLogoIcon
				aria-hidden
				className="pointer-events-none absolute -top-[26%] -left-[8%] h-auto w-[62%] text-white opacity-10 md:-top-[35%] md:-left-[19%] md:w-full"
			/>
			{/* An absolutely positioned sibling paints over in-flow content, so this needs its own order. */}
			<div className="relative z-10">
				<StatValue value={props.value} label={t`Microdonations received`} valueClassName={FLUID_FIGURE} />
			</div>
		</div>
	);
}

export function PartnerNgosTile(props: TileProps & { value: string }) {
	const { t } = useLingui();
	return (
		<div style={props.style} className={cn('bg-ink flex flex-col rounded-3xl p-6 text-white md:p-8', props.className)}>
			<Heart className="text-olive size-6 fill-current" />
			<div className="mt-auto pt-6">
				{/* `StatValue`'s sizes, repeated: this figure is olive on ink and has no seam there. */}
				<p className="text-olive text-4xl font-semibold tabular-nums md:text-[3.5rem]/[1.05]">{props.value}</p>
				<p className="mt-2 text-sm font-medium text-white/80 md:text-2xl">{t`Partner NGOs`}</p>
			</div>
		</div>
	);
}

/** One 220x384 request for every tile, sized for the larger consumer: the mobile tile crops it, never upscales. */
export function PhotoTile(props: TileProps & { src: string; alt: string }) {
	return (
		<div style={props.style} className={cn('overflow-hidden rounded-3xl', props.className)}>
			<Image
				src={props.src}
				alt={props.alt}
				height={384}
				aspectRatio={220 / 384}
				objectFit="cover"
				className="h-full w-full object-cover!"
			/>
		</div>
	);
}
